import { useEffect, useRef } from "react";

import { useSaveUserFilter } from "@/components/daylight-insight/api/useSaveUserFilter";
import type {
  DashboardMetricsParams,
  SavedDashboardFilters,
  UserFilterDetails,
} from "@/components/daylight-insight/api/types";

/**
 * Keeps the dashboard's SAVED filter selection in step with the filters the user
 * actually has applied, in the one case where nothing else does.
 *
 * ── The problem ────────────────────────────────────────────────────────────
 * `[DayLight].[GetMetricsValuesForDashboard]` persists the filter selection as a
 * side effect of returning metric values. That makes a WRITE depend on a READ
 * happening — and a read is precisely what a cache is allowed to skip. Since the
 * filter selection IS the metrics query key, returning to a combination already
 * in cache serves from memory, the SP never runs, and the stored row keeps
 * whatever was last requested over the wire.
 *
 * Clearing every filter is the common way to hit this (it returns to the
 * unfiltered combination the dashboard opened on, which is always cached), but
 * it is not the only one: any `A → B → A` within the metrics `staleTime` leaves
 * the server holding `B`. The next visit then re-applies filters the user had
 * already removed, because `dashboard/custom-views` faithfully reads back the
 * row nobody updated.
 *
 * ── The rule ───────────────────────────────────────────────────────────────
 * Write ONLY when both hold:
 *
 *   1. the metrics response was served from cache — so the SP did not run, and
 *      nothing else is going to record this selection; and
 *   2. the selection differs from what the server is known to hold — so a plain
 *      dashboard revisit, which is a cache hit but changes nothing, stays silent.
 *
 * A Focus-driven selection (`saveUserFilterPreferences: false`) is never
 * written: the SP is told not to save it, so neither does this hook, and the
 * server is not counted as holding it.
 *
 * On a real network metrics call the SP still does the writing and this hook
 * stays out of the way entirely. That keeps the extra endpoint off the hot path:
 * it fires on filter changes that cost no metrics request, which is exactly the
 * set of changes that would otherwise be lost.
 */

/** A filter map with empty lists dropped — `{ payor: [] }` and `{}` both mean
 *  "no payor filter", and must neither compare nor store differently. */
const compact = (f: Record<string, string[]> | null | undefined): UserFilterDetails =>
  Object.fromEntries(Object.entries(f ?? {}).filter(([, v]) => v.length > 0));

/**
 * The comparable form of a selection: one entry per filter key, keys and values
 * both sorted. Order-insensitive because the controls and the stored row list
 * the same selection in whatever order each happened to build it, and a reorder
 * is not a change the user made — comparing unsorted would write on every cache
 * hit. JSON keeps keys and values delimited, so `["a"] + ["b"]` can never
 * collide with `["ab"]`.
 *
 * DATES ARE DELIBERATELY ABSENT, here and in the stored document. The metrics SP
 * does not write a date range into the `UserFilter` row, so this must not
 * either: `SaveAppUserPerfs` replaces `PerfDetails` wholesale, and adding dates
 * would make the range newly sticky per dashboard. A date change still moves the
 * metrics query key, so it refetches and the SP records the selection itself;
 * it just never triggers a write from here.
 */
const signature = (f: Record<string, string[]> | null | undefined): string =>
  JSON.stringify(
    Object.entries(compact(f))
      .map(([k, v]): [string, string[]] => [k, [...v].sort()])
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
  );

const signatureOfParams = (p: DashboardMetricsParams): string => signature(p.filters);

/** What the server row holds. A dashboard with no saved row yet reads as "no
 *  filters", which is what an unfiltered dashboard would send anyway — so a
 *  first visit that changes nothing still writes nothing. */
const signatureOfSaved = (f: SavedDashboardFilters | null): string =>
  signature(f?.values);

/** The document to store — the `@Filters` map itself, the same shape the
 *  metrics SP writes, so what is written is exactly what "changed" was decided
 *  on. `{}` is how "every filter cleared" is stored. */
const detailsFor = (p: DashboardMetricsParams): UserFilterDetails => compact(p.filters);

/** Coalesces the burst a multi-select produces — every checkbox is its own
 *  state change, and walking back through cached combinations would otherwise
 *  write once per click. Only the selection the user settles on is stored. */
const DEBOUNCE_MS = 600;

interface Args {
  /** The params the metrics query was given, or null while it is idle. Null
   *  means there is no selection to talk about yet. */
  params: DashboardMetricsParams | null;
  /** `query.isFetching` for the metrics query. TRUE on the very render the key
   *  changes when a fetch will follow, which is what makes "no fetch + data
   *  present" a sound reading of "served from cache". */
  fetching: boolean;
  /** Whether the metrics query currently holds data. False while a request is
   *  outstanding and after a failure — neither is a cache hit. */
  hasData: boolean;
  /** The row `dashboard/custom-views` returned, i.e. what the server held when
   *  this dashboard was opened. Seeds the comparison — and only that; nothing
   *  from it is written back. */
  savedFilters: SavedDashboardFilters | null;
  /** True once the saved filters are in the controls (`filtersApplied` &&
   *  `perfs.ready`). Before that the controls are still being populated FROM the
   *  server, and writing back would be a loop. */
  ready: boolean;
}

export function useDashboardFilterSync({
  params,
  fetching,
  hasData,
  savedFilters,
  ready,
}: Args): void {
  const { saveUserFilter } = useSaveUserFilter();

  /** The signature the server is known to hold. Seeded from the row that was
   *  read on open, then advanced by whatever writes it: a metrics request that
   *  actually reached the SP, or a save of our own. */
  const persisted = useRef<string | null>(null);
  /** Whether a network fetch was observed for the params currently in hand. A
   *  key we never saw fetch, and that has data, came from the cache. */
  const seen = useRef<{ key: string; fetched: boolean } | null>(null);
  /** The write waiting out the debounce, so it can be flushed on unmount rather
   *  than dropped when the user changes a filter and immediately switches away. */
  const pending = useRef<(() => void) | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = () => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const run = pending.current;
    pending.current = null;
    run?.();
  };

  // Seed once the saved row has been read. `ready` guarantees the controls have
  // already been populated from it, so this is the state both sides agree on.
  useEffect(() => {
    if (!ready || persisted.current !== null) return;
    persisted.current = signatureOfSaved(savedFilters);
  }, [ready, savedFilters]);

  useEffect(() => {
    if (!ready || !params || params.dashboardID == null) return;

    // `params` is the metrics query key, so its JSON identifies the cache entry
    // this render is reading. Service line and date range are in here as well as
    // the filters — a change to either is a different entry, and correctly reads
    // as a fresh miss rather than a cache hit on the old one.
    const key = JSON.stringify(params);
    if (seen.current?.key !== key) {
      seen.current = { key, fetched: fetching };
    } else if (fetching) {
      seen.current.fetched = true;
    }

    // Still resolving, or resolved into an error: nothing to conclude yet.
    if (fetching || !hasData) return;

    // A Focus selection is not the user's preference — the SP skipped saving
    // it, so `persisted` stays on what the server still holds.
    if (params.saveUserFilterPreferences === false) return;

    if (seen.current.fetched) {
      // The request went out, so the SP ran and recorded this selection itself.
      // Nothing to write — just note what the server now holds, so a later
      // return to a cached combination is measured against the right baseline.
      persisted.current = signatureOfParams(params);
      return;
    }

    // Served from cache. The SP did not run, so this selection is stored only if
    // it was already what the server had.
    const current = signatureOfParams(params);
    if (persisted.current === current) return;

    const dashboardID = params.dashboardID;
    const details = detailsFor(params);
    // Claim the signature up front: a second cache hit for the same selection
    // arriving while the write is in flight must not queue a duplicate.
    persisted.current = current;

    pending.current = () => {
      void saveUserFilter(dashboardID, details).then((ok) => {
        // A failed write must not leave the server holding filters we have told
        // ourselves it no longer has — the next cache hit should try again.
        if (!ok && persisted.current === current) persisted.current = null;
      });
    };
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      flush();
    }, DEBOUNCE_MS);
  }, [ready, params, fetching, hasData, saveUserFilter]);

  // Switching dashboards unmounts the page. Anything still waiting out the
  // debounce is the user's last filter change and has to go out.
  // `flush` only ever touches refs, so the mount-only dependency list is sound.
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => () => flushRef.current(), []);
}
